<template>
  <BaseWidget
    :title="content.title"
    freeMode
    :widgetId="content.id"
    :edit="edit"
    :isWidgetLoading="isWidgetLoading"
    :content="content"
    :getWidgetReport="getWidgetReport"
  >
    <Summary
      v-model="metrics"
      @input="onUpdateMetrics"
      @onOrderChange="onOrderChange"
      :items="itemsFiltered"
      :module="PLAN_MODULE"
      :previous-items="previousItems"
      class="summary-widget"
      :is-edit-mode="edit"
      :is-widget-loading="isWidgetLoading"
      :show-trends="false"
      :use-compact-format="content.settings.compactFormat"
    />
  </BaseWidget>
</template>

<script lang="ts">
import Vue from "vue";
import { mapGetters } from "vuex";

import ProjectWidgetApi from "@/classes/ProjectWidgetApi";
import Summary from "@/components/summary/Summary.vue";
import BaseWidget from "@/components/widgets/plan/BaseWidget.vue";
import { PLAN_MODULE } from "@/constants";
import csvExport, { buildFileName, buildHeaderRows } from "@/helpers/csvExport";
import Lang from "@/lang";
import { metricsDictionary } from "@/settings/planMetricsDictionary";
import { SummaryItem } from "@/settings/summary/summaryPlan";
import { ContentWidget } from "@/types/layouts";

export default Vue.extend({
  name: "SummaryWidget",

  props: {
    content: Object as () => ContentWidget,
    value: Object,
    edit: Boolean,
  },

  components: {
    Summary,
    BaseWidget,
  },

  beforeMount() {
    this.getData();
  },

  data() {
    return {
      PLAN_MODULE,
      Lang,
      items: [] as SummaryItem[],
      previousItems: [] as SummaryItem[],
      metrics: (this.content?.settings?.metrics || []) as string[],
      isWidgetLoading: false,
      areBadgesLoading: false,
    };
  },

  computed: {
    ...mapGetters("projectOutput", ["projectFiltered", "filters"]),

    itemsFiltered(): SummaryItem[] {
      return (this.metrics as string[]).map((metric: string) => {
        return (
          this.items.find((item) => {
            return metric === item.name;
          }) || { name: metric, value: null }
        );
      });
    },
  },

  methods: {
    async getData(): Promise<void> {
      this.isWidgetLoading = true;

      try {
        await this.getCurrentData();
      } finally {
        this.isWidgetLoading = false;
      }
    },

    async getCurrentData(): Promise<void> {
      const projectWidgetApi = new ProjectWidgetApi();
      const data: Record<string, number | null>[] =
        (await projectWidgetApi.getSummaryData(this.projectFiltered, this.metrics)) || [];

      if (!data?.length) {
        return;
      }

      this.items = Object.keys(data[0]).map((key) => {
        return {
          name: key,
          value: data[0][key] ?? 0,
          badge: { isShown: false },
        };
      });
    },

    onUpdateMetrics(updatedMetrics: string[], doDataRefresh: boolean): void {
      this.$emit("inputSummary", {
        ...this.value,
        settings: { ...this.content?.settings, metrics: updatedMetrics },
      });
      if (doDataRefresh) {
        this.getData();
      }
    },

    getWidgetReport(): void {
      const headers = ["Metric", "Value"];

      const rows = this.metrics.map((name: string) => {
        const dict = metricsDictionary.find((m) => m.value === name);
        const title = dict?.title || name;
        const fmt = dict?.formatter;

        const curRaw = this.items.find((i) => i.name === name)?.value ?? null;
        const cur = typeof curRaw === "number" && typeof fmt === "function" ? fmt(curRaw) : curRaw;

        return [title, cur];
      });

      const csvContent: unknown[][] = [];
      csvContent.push(...buildHeaderRows(this.content?.title));
      csvContent.push([]);
      csvContent.push(headers);
      csvContent.push(...rows);

      const fileName = buildFileName(this.content?.title);
      csvExport(csvContent, fileName);
    },

    onOrderChange({ newIndex, oldIndex }: Record<string, number>) {
      const movedElement = this.metrics.splice(oldIndex, 1);
      this.metrics.splice(newIndex, 0, movedElement[0]);
    },
  },

  watch: {
    projectFiltered: {
      deep: true,
      immediate: true,
      async handler(p): Promise<void> {
        if (!p || !Array.isArray(p.business_goals)) {
          return;
        }
        this.isWidgetLoading = true;
        await this.getData();
        this.isWidgetLoading = false;
      },
    },

    filters: {
      deep: true,
      async handler(): Promise<void> {
        this.isWidgetLoading = true;
        await this.getData();
        this.isWidgetLoading = false;
      },
    },

    "content.settings.metrics": {
      handler(val: string[] | undefined) {
        this.metrics = (val || []) as string[];
      },
      deep: false,
      immediate: true,
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme";
@import "@/scss/custom_variables.scss";

.summary-widget {
  &__edit-title {
    font-size: 20px;
    font-weight: 600;
    line-height: 32px;
    margin-bottom: $space-medium;

    &::before {
      content: "";
      display: inline-block;
      height: 32px;
      width: 8px;
      margin-right: $space-small;
      vertical-align: bottom;
      background-color: var(--v-secondary-base);
      border-radius: 4px;
    }
  }

  &__btn-block {
    display: flex;
    justify-content: flex-end;
    padding-top: $space-medium;
  }
}
</style>
